import { AnchorButton } from '@payaca/ambrosia';
import { FC, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import { usePermissions } from '@/hooks/usePermissions';
import CreateEditTaskSidebar from '@/ui/components/createEditTaskSidebar/CreateEditTaskSidebar';
import TaskReadCompleteDrawer from '@/ui/components/taskReadCompleteDrawer/TaskReadCompleteDrawer';

import { TasksPermissions } from '@payaca/permissions/tasks/tasks.permissions';
import { useDeal } from '@payaca/store/hooks/appState';
import useGetProjectByIdOverview from '../../../api/queries/project/useGetProjectByIdOverview';
import useGetProjectTasks from '../../../api/queries/project/useGetProjectTasks';
import { storedAssigneeFilters } from '../../../api/queries/tasks/assigneeFilter';
import { GetTasksInput } from '../../../gql/graphql';
import {
  getTasksCompletionStatusesLocalStorageKey,
  getTasksEventRelationshipStatusesLocalStorageKey,
  getTasksTypesLocalStorageKey,
  getTasksUserAssignmentKeyLocalStorageKey,
} from '../../../helpers/localStorageKeyHelper';
import ListedTasks, {
  ListedTasksPaginationInput,
  ListedTasksTTask,
} from '../listedTasks/ListedTasks';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';

const tasksUserAssignmentKeyLocalStorageKey =
  getTasksUserAssignmentKeyLocalStorageKey();
const tasksCompletionStatusesLocalStorageKey =
  getTasksCompletionStatusesLocalStorageKey();
const tasksEventRelationshipStatusesLocalStorageKey =
  getTasksEventRelationshipStatusesLocalStorageKey();
const tasksTypesLocalStorageKey = getTasksTypesLocalStorageKey();

const COMPLETION_STATUSES_PARAM = 'completionStatuses';
const EVENT_RELATIONSHIP_STATUSES_PARAM = 'eventRelationshipStatuses';

/**
 * Path to a Project's Tasks tab with the given filters applied. Without them
 * the tab shows whichever filters the user last chose, which can hide the
 * Tasks a link promised.
 */
export const dealTasksPath = (
  projectId: string,
  filters: {
    completionStatuses: string[];
    eventRelationshipStatuses: string[];
  }
) => {
  const params = new URLSearchParams({
    [COMPLETION_STATUSES_PARAM]: filters.completionStatuses.join(','),
    [EVENT_RELATIONSHIP_STATUSES_PARAM]:
      filters.eventRelationshipStatuses.join(','),
  });

  return `/projects/${projectId}/tasks?${params.toString()}`;
};

export const DealTasks: FC<{
  projectId: string;
  onDealUpdateSuccess: () => void;
}> = ({ projectId, onDealUpdateSuccess }) => {
  const deal = useDeal();
  const history = useHistory();
  const { search } = useLocation();
  const { path, url } = useRouteMatch();
  const { userHasRequiredPermission } = usePermissions();
  const { project } = useGetProjectByIdOverview(projectId);

  const isAddingOrEditingTask = useRouteMatch<{
    taskId: string;
  }>({
    path: `${path}/:taskId`,
    exact: true,
  });
  const initialUserAssignments = userHasRequiredPermission([
    TasksPermissions.GET_TASKS,
  ])
    ? storedAssigneeFilters(
        localStorage.getItem(tasksUserAssignmentKeyLocalStorageKey)
      )
    : [];
  // A filter given in the URL applies to this visit only. It is deliberately
  // not written to localStorage, which the Tasks page shares — arriving via a
  // link should not change what the user sees everywhere else.
  const searchParams = new URLSearchParams(search);
  const filterFromSearchParams = (param: string) =>
    searchParams
      .get(param)
      ?.split(',')
      .filter((x) => x != '');

  const initialCompletionStatuses = filterFromSearchParams(
    COMPLETION_STATUSES_PARAM
  ) ||
    localStorage
      .getItem(tasksCompletionStatusesLocalStorageKey)
      ?.split(',')
      .filter((x) => x != '') || ['to do', 'in progress'];
  const initialEventRelationshipStatuses =
    filterFromSearchParams(EVENT_RELATIONSHIP_STATUSES_PARAM) ||
    localStorage
      .getItem(tasksEventRelationshipStatusesLocalStorageKey)
      ?.split(',')
      .filter((x) => x != '') ||
    [];
  const initialTypes = localStorage
    .getItem(tasksTypesLocalStorageKey)
    ?.split(',')
    .filter((x) => x != '');

  const [getTasksPagination, setGetTasksPagination] =
    useState<ListedTasksPaginationInput>({
      limit: 100,
      offset: 0,
    });
  const [getTasksInput, setGetTasksInput] = useState<GetTasksInput>({
    searchTerm: '',
    assignedTo: initialUserAssignments,
    completionStatuses: initialCompletionStatuses,
    eventRelationshipStatuses: initialEventRelationshipStatuses,
    sortBy: 'deadlineDate',
    sortDirection: 'asc',
    types: initialTypes,
  });

  const onUpdateInputData = (inputData: GetTasksInput) => {
    if ('assignedTo' in inputData) {
      localStorage.setItem(
        tasksUserAssignmentKeyLocalStorageKey,
        inputData.assignedTo?.join(',') || ''
      );
    }
    if ('completionStatuses' in inputData) {
      localStorage.setItem(
        tasksCompletionStatusesLocalStorageKey,
        inputData.completionStatuses?.join(',') || ''
      );
    }
    if ('eventRelationshipStatuses' in inputData) {
      localStorage.setItem(
        tasksEventRelationshipStatusesLocalStorageKey,
        inputData.eventRelationshipStatuses?.join(',') || ''
      );
    }
    if ('types' in inputData) {
      localStorage.setItem(
        tasksTypesLocalStorageKey,
        inputData.types?.join(',') || ''
      );
    }
    setGetTasksInput((x) => ({
      ...x,
      ...inputData,
    }));
  };
  const {
    data: tasksData,
    isLoading: isLoadingTasks,
    refetch: refetchTasks,
  } = useGetProjectTasks(projectId, getTasksInput, getTasksPagination);

  const [isTaskPersisted, setIsTaskPersisted] = useState(false);

  useEffect(() => {
    void refetchTasks();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'refetchTasks'
  }, [getTasksInput, getTasksPagination]);

  const listedTasks = useMemo(() => {
    return tasksData?.projectById.tasks.items
      .map((task) => {
        const t = {
          id: task.id,
          name: task.name,
          deadlineDate: task.deadline,
          createdByUserId: task?.createdBy?.id,
          events:
            task?.events.map((event) => ({
              id: event.id,
              name: event.name,
            })) || [],
          assignee:
            task.assignedTo?.__typename === 'User'
              ? {
                  id: task.assignedTo.id,
                  firstName: task.assignedTo.firstName,
                  lastName: task.assignedTo.lastName,
                  email: task.assignedTo.email,
                  colour: task.assignedTo.colour,
                }
              : undefined,
          assigneeTeam:
            task.assignedTo?.__typename === 'Team'
              ? { id: task.assignedTo.id, name: task.assignedTo.name }
              : null,
          completionStatus: task.completionStatus,
        };
        switch (task.__typename) {
          case 'ChecklistTask': {
            return {
              ...t,
              __typename: task.__typename,
            };
          }
          case 'FormTask': {
            return {
              ...t,
              __typename: task.__typename,
            };
          }
          case 'LegacyFormTask': {
            return {
              ...t,
              __typename: task.__typename,
            };
          }
          case 'MaterialsListTask': {
            return {
              ...t,
              __typename: task.__typename,
            };
          }
          case 'CustomFieldsTask': {
            return {
              ...t,
              __typename: task.__typename,
            };
          }
        }
      })
      .filter((t) => !!t) as ListedTasksTTask[];
  }, [tasksData?.projectById.tasks.items]);

  const headerContent = useMemo(() => {
    return {
      heading: 'Tasks',
      subHeading: '',
      buttons: (
        <>
          {!deal?.archivedAt && (
            <PermissionGuard
              renderIfHasPermissions={[
                TasksPermissions.PERSIST_SELF_ASSIGNED_DEAL_TASK,
                TasksPermissions.PERSIST_TASK,
              ]}
            >
              <AnchorButton
                as={Link}
                to={{ pathname: `${url}/new`, search }}
                size={'sm'}
              >
                Create task
              </AnchorButton>
            </PermissionGuard>
          )}
        </>
      ),
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'url'
  }, [deal?.archivedAt, search]);

  return (
    <div className="@container">
      <ListedTasks
        getTasksInput={getTasksInput}
        setGetTasksInput={onUpdateInputData}
        getTasksPagination={getTasksPagination}
        setGetTasksPagination={setGetTasksPagination}
        listedTasks={listedTasks}
        isLoadingTasks={isLoadingTasks}
        onPersistTaskSuccess={() => void refetchTasks()}
        hideProjectAndCustomerColumn
        totalCount={tasksData?.projectById.tasks.totalCount || 0}
        headerContent={headerContent}
      />
      <CreateEditTaskSidebar
        projectId={project?.id ? +project.id : undefined}
        isOpen={isAddingOrEditingTask?.params.taskId === 'new'}
        onClose={() => {
          history.push({ pathname: url, search });
        }}
        onSuccess={() => {
          onDealUpdateSuccess();
        }}
      />
      <TaskReadCompleteDrawer
        isOpen={
          isAddingOrEditingTask
            ? isAddingOrEditingTask?.params.taskId !== 'new'
            : false
        }
        onClose={() => {
          history.push({ pathname: url, search });
          if (isTaskPersisted) {
            void refetchTasks();
            onDealUpdateSuccess();
            setIsTaskPersisted(false);
          }
        }}
        taskId={
          isAddingOrEditingTask?.params.taskId
            ? parseInt(isAddingOrEditingTask.params.taskId)
            : undefined
        }
        onUpdateTaskSuccess={() => setIsTaskPersisted(true)}
        onDeleteTaskSuccess={() => {
          void refetchTasks();
          onDealUpdateSuccess();
        }}
      />
    </div>
  );
};
